import { isAppLocale, registerCatalogueLoader, type AppLocale } from '@floorfile/i18n'
import { fillCatalogues } from './catalogue-fill'
import { ENUM_NAMESPACE, enumTranslationLoaders } from '@floorfile/i18n/enums'
import { UI_NAMESPACE, uiTranslationLoaders } from '@floorfile/ui'

/**
 * Discovery and loading of every non-English catalogue, for one locale.
 *
 * ## Why a glob and not a hand-maintained list
 *
 * A list of 36 imports is a list somebody forgets to add to, and the symptom of
 * forgetting is one feature silently rendering English — indistinguishable from
 * an untranslated feature, which is a legitimate state. `import.meta.glob`
 * cannot forget.
 *
 * ## One chunk per catalogue — a screen waits for its own words only
 *
 * D-L6 (owner decision, 2026-08-01): only the ACTIVE locale is precached. Each
 * `copy.<locale>.ts` is therefore reached through a dynamic `import()`, which
 * is what `import.meta.glob` emits — the `no-static-locale-imports` lint rule
 * enforces that nothing anywhere reaches them the static way.
 *
 * Until 2026-09-25 `manualChunks` grouped them into ONE `locale-<code>` chunk
 * (169 kB gz for French, every string in the product) and the first French
 * screen waited for all of it. The owner chose to split it: `manualChunks`
 * now names one `locale-<code>-<namespace>` chunk per catalogue, and
 *
 *  1. every loader is DECLARED below at import time (nothing is fetched), so
 *     the very first render already knows it can wait for its words;
 *  2. `useCopy` waits for its own namespace only (`catalogueGate`, at most
 *     `CATALOGUE_WAIT_MS`, then English);
 *  3. `loadCatalogues` then fills in the rest of the language in the
 *     background, once the screen is up, so the floor screens are still in
 *     the user's language offline (the chunks are runtime-cached, D-L6).
 *
 * ## The namespace comes from the DIRECTORY, never from the file
 *
 * `features/checks/copy.fr.ts` registers under `checks`. Nothing in the
 * translation file names its own namespace, so a copy-pasted catalogue cannot
 * register under the namespace of the file it was copied from — which would
 * shadow one feature's translations with another's and still render perfectly
 * plausible French. `copy-namespaces.test.ts` asserts each `copy.ts`'s
 * `defineCopy` argument matches the same directory.
 */
type CatalogueLoader = () => Promise<unknown>

// Relative to this file: `src/**/copy.<locale>.ts` — the app shell, every
// feature slice, `components/locale` and `lib/errors`.
const MODULES = import.meta.glob('../**/copy.*.ts') as Record<string, CatalogueLoader>

/** This file's own directory, and therefore the namespace of a `./`-keyed hit. */
const OWN_NAMESPACE = 'app'

/**
 * Glob keys are relative to THIS file, so the shell's own catalogue arrives as
 * `./copy.fr.ts` — with no directory segment at all — while everything else
 * arrives as `../features/checks/copy.fr.ts`.
 *
 * Caught in the browser, not by a type or a test: the first namespace matcher
 * required a directory before `copy.`, so `./copy.fr.ts` simply did not match
 * and the app shell — the LARGEST catalogue in the repo, 117 keys — was
 * silently never registered. Every other slice switched to French and the
 * chrome stayed English, which reads exactly like "the shell has not been
 * migrated yet". Nothing threw, nothing warned, and no test could see it
 * because the glob only exists inside a bundler.
 */
function normalizeKey(key: string): string {
  const path = key.replace(/\\/g, '/')
  return path.startsWith('./') ? `${OWN_NAMESPACE}/${path.slice(2)}` : path.replace(/^(\.\.\/)+/, '')
}

interface CatalogueEntry {
  readonly namespace: string
  readonly locale: AppLocale
  readonly load: CatalogueLoader
}

const GLOBBED: readonly CatalogueEntry[] = Object.entries(MODULES).flatMap(([key, load]) => {
  const match = /(?:^|\/)([^/]+)\/copy\.([^./]+)\.tsx?$/.exec(normalizeKey(key))
  const namespace = match?.[1]
  const locale = match?.[2]
  // `en` is excluded because it is not a chunk at all: `copy.ts` is statically
  // imported and rides in its feature chunk, precached, which is what makes it
  // a usable fallback. Anything that is not a known locale is not a catalogue.
  if (!namespace || !isAppLocale(locale) || locale === 'en') return []
  return [{ namespace, locale, load }]
})

/**
 * `@floorfile/ui`'s own catalogue, which the glob above cannot reach.
 *
 * `import.meta.glob` is rooted in this app's `src`, so the design system's ~50
 * strings — the pager, the bulk bar, the four query states, the data-quality
 * chips — were outside the discovery mechanism entirely. They are registered
 * from a map the package exports instead, under the namespace the package
 * declares in one place (`UI_NAMESPACE`). The loaders are still `import()`, so
 * `copy.fr.ts` there joins the same `locale-fr` chunk as everything here.
 */
const packageEntries = (
  namespace: string,
  loaders: Readonly<Partial<Record<string, CatalogueLoader>>>,
): readonly CatalogueEntry[] =>
  Object.entries(loaders).flatMap(([locale, load]) =>
    load && isAppLocale(locale) && locale !== 'en' ? [{ namespace, locale, load }] : [],
  )

const PACKAGE_ENTRIES: readonly CatalogueEntry[] = [
  ...packageEntries(UI_NAMESPACE, uiTranslationLoaders),
  // `@floorfile/i18n/enums` — the words for contract enum VALUES, shared with
  // the Worker so the archived PDF and the screen say the same thing. Same
  // reason as the design system above: the glob is rooted in this app's `src`
  // and cannot see a package, so a package catalogue registers explicitly or
  // not at all. Forgetting this line renders every status, priority and
  // severity in the product in English while the rest of the screen is French
  // — which reads exactly like an unmigrated slice.
  ...packageEntries(ENUM_NAMESPACE, enumTranslationLoaders),
]

const ENTRIES: readonly CatalogueEntry[] = [...GLOBBED, ...PACKAGE_ENTRIES]

// (1) Declared at import time — before any render — and fetch nothing.
for (const entry of ENTRIES) registerCatalogueLoader(entry.namespace, entry.locale, entry.load)

/**
 * (3) Fill in the rest of `locale` in the background — every catalogue no
 * screen has already waited for — once the first screen is up
 * (`catalogue-fill.ts`). A STATIC import on purpose: loaded lazily it became
 * a desk-only chunk outside the offline precache, to save 0.1 kB of entry.
 */
export function loadCatalogues(locale: AppLocale): Promise<void> {
  return fillCatalogues(
    locale,
    ENTRIES.filter((entry) => entry.locale === locale).map((entry) => entry.namespace),
  )
}

/** Test/diagnostic view of what the glob found. */
export function catalogueEntries(): readonly { namespace: string; locale: AppLocale }[] {
  return ENTRIES.map(({ namespace, locale }) => ({ namespace, locale }))
}
